import React, { useState, useEffect } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { StatusBadge } from 'src/components/common/StatusBadge';
import { useSearchParams } from 'react-router-dom';
import DataTable from 'src/components/ui/data-table';
import { Button } from 'src/components/ui/button';
import AppSelect from 'src/components/common/AppSelect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useAhedgeUsersQuery } from 'src/services/api/queries/userQueries';
import { UserService } from 'src/services/api/UserService';
import { Card, CardContent } from 'src/components/ui/card';
import {
  Calendar,
  UserRoundPlus,
  MoreHorizontal,
  UserX,
  Users,
  Mail,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import { AddUserModal } from 'src/components/Modals/AddUserModal';
import { AssignRoleModal } from 'src/components/Modals/AssignRoleModal';
import { useToast } from 'src/hooks/use-toast';

const DEFAULT_PAGE_SIZE = 10;

// Elevated roles get the brand tint; everything else is neutral — matches sample HTML .rpill
const ELEVATED_ROLES = new Set();
const getRolePillClass = (v) =>
  ELEVATED_ROLES.has(v)
    ? 'bg-brand-bg-subtle text-primary border-primary/[0.12]'
    : 'bg-muted text-muted-foreground border-border';

const NameCell = ({ row }) => {
  const name = [
    row.original.first_name,
    row.original.middle_name,
    row.original.last_name,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className="text-[13px] font-[550] text-foreground-strong">
      {name}
    </span>
  );
};

const EmailCell = ({ value }) => (
  <div className="flex items-center gap-1.5">
    <Mail
      className="w-3.5 h-3.5 text-primary flex-shrink-0"
      strokeWidth={1.8}
    />
    <span className="text-[12.5px] text-[#2168B2]">{value}</span>
  </div>
);

const RolePill = ({ role }) => (
  <span
    className={`inline-flex items-center px-[9px] py-[3px] rounded-[6px] text-[11px] font-[650] tracking-[.01em] whitespace-nowrap border ${getRolePillClass(role.value)}`}
  >
    {role.label}
  </span>
);

// Same pill shape/sizing as RolePill above (so the column doesn't jump
// around row to row) but dashed and muted — reads as "nothing assigned
// yet", not "data missing/broken" the way a bare "—" does, and doubles as
// a visual nudge toward the row's own "Assign Role" action.
const NoRolePill = () => (
  <span className="inline-flex items-center px-[9px] py-[3px] rounded-[6px] text-[11px] font-[550] tracking-[.01em] whitespace-nowrap border border-dashed border-border text-muted-foreground">
    No role
  </span>
);

const RoleCell = ({ roles }) => {
  if (!roles || roles.length === 0) return <NoRolePill />;

  const [first, ...rest] = roles;

  return (
    <div className="flex items-center gap-1.5">
      <RolePill role={first} />
      {rest.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="px-[7px] py-[3px] rounded-[6px] border border-border bg-muted text-[11px] font-[650] tracking-[.01em] text-muted-foreground hover:bg-brand-bg-subtle hover:text-primary hover:border-primary/30 transition-colors cursor-pointer"
            >
              +{rest.length}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-0 p-2">
            <div className="flex flex-wrap gap-1">
              {rest.map((role) => (
                <RolePill key={role.value} role={role} />
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

const ActiveStatusCell = ({ value }) => (
  <StatusBadge value={value ? 'ACTIVE' : 'INACTIVE'} />
);

const DateCell = ({ value }) => {
  if (!value)
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()))
    return <span className="text-[12.5px] text-muted-foreground">—</span>;
  const formatted = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  return (
    <div className="flex items-center gap-1.5">
      <Calendar
        className="w-3.5 h-3.5 text-muted-foreground"
        strokeWidth={1.8}
      />
      <span className="text-[12.5px] text-gray-500">{formatted}</span>
    </div>
  );
};

const ActionCell = ({
  row,
  onToggleStatus,
  isToggling,
  onAssignRoles,
  isAssigningRoles,
}) => {
  const isActive = row.original.is_active;
  const userId = row.original.id;
  const userName = [row.original.first_name, row.original.last_name]
    .filter(Boolean)
    .join(' ');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);

  const handleConfirmAction = () => {
    onToggleStatus(userId);
    setShowConfirmDialog(false);
  };

  const handleAssignRolesSubmit = async (id, roles) => {
    await onAssignRoles(id, roles);
    setShowAssignRoleModal(false);
  };

  if (!isActive) {
    return <span className="text-gray-300 text-sm">—</span>;
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity rounded-md hover:bg-muted"
          >
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-3.5 w-3.5 text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setShowAssignRoleModal(true)}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            Assign Role
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShowConfirmDialog(true)}
            disabled={isToggling}
            className="text-destructive focus:text-destructive"
          >
            <UserX className="mr-2 h-4 w-4" />
            Deactivate
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <div className="flex flex-col items-center gap-3 mb-1">
              <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle
                  className="w-6 h-6 text-red-500"
                  strokeWidth={2}
                />
              </div>
              <DialogTitle className="text-center">Deactivate User</DialogTitle>
            </div>
            <DialogDescription className="text-center text-[13px]">
              Are you sure you want to deactivate
              {userName ? ` ${userName}` : ' this user'}? They will lose access
              to the platform immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-center mt-1">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isToggling}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmAction}
              disabled={isToggling}
            >
              {isToggling ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AssignRoleModal
        isOpen={showAssignRoleModal}
        user={row.original}
        onSubmit={handleAssignRolesSubmit}
        onClose={() => setShowAssignRoleModal(false)}
        loading={isAssigningRoles}
      />
    </>
  );
};

const getColumns = (
  onToggleStatus,
  isToggling,
  onAssignRoles,
  isAssigningRoles
) => [
  {
    key: 'first_name',
    header: 'Name',
    minWidth: '200px',
    render: (_v, row) => <NameCell row={{ original: row }} />,
  },
  {
    key: 'email',
    header: 'Email',
    minWidth: '220px',
    render: (v) => <EmailCell value={v} />,
  },
  {
    key: 'roles',
    header: 'Role',
    width: '220px',
    minWidth: '180px',
    render: (v) => <RoleCell roles={v} />,
  },
  {
    key: 'is_active',
    header: 'Status',
    width: '120px',
    minWidth: '110px',
    render: (v) => <ActiveStatusCell value={v} />,
  },
  {
    key: 'created_at',
    header: 'Created',
    width: '150px',
    minWidth: '130px',
    render: (v) => <DateCell value={v} />,
  },
  {
    key: 'actions',
    header: '',
    width: '60px',
    minWidth: '60px',
    render: (_v, row) => (
      <ActionCell
        row={{ original: row }}
        onToggleStatus={onToggleStatus}
        isToggling={isToggling}
        onAssignRoles={onAssignRoles}
        isAssigningRoles={isAssigningRoles}
      />
    ),
  },
];

const AhedgeUsers = () => {
  const { toast } = useToast();
  const [selectedRole, setSelectedRole] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isAssigningRoles, setIsAssigningRoles] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useAhedgeUsersQuery(selectedRole, currentPage, currentPageSize);

  const users = usersData?.users || [];
  const totalItems = usersData?.pagination?.total || 0;

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'ahedge_ta', label: 'TA' },
    { value: 'ahedge_cse', label: 'CSE' },
    { value: 'ahedge_interviewer', label: 'Interviewer' },
  ];

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    updateUrlParams({ page: 1 });
  };

  const handleToggleStatus = async (userId) => {
    setIsToggling(true);
    try {
      await UserService.deactivateUser(userId);
      toast({
        title: 'Success',
        description: 'User deactivated successfully!',
      });
      refetch();
    } catch (deactivateError) {
      console.error('Error deactivating user:', deactivateError);
      toast({
        title: 'Error',
        description:
          deactivateError.response?.data?.message ||
          'Failed to deactivate user',
        variant: 'destructive',
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleAssignRoles = async (userId, roles) => {
    setIsAssigningRoles(true);
    try {
      await UserService.assignRoles(userId, roles);
      toast({
        title: 'Success',
        description: 'Roles updated successfully!',
      });
      refetch();
    } catch (assignRolesError) {
      console.error('Error assigning roles:', assignRolesError);
      toast({
        title: 'Error',
        description:
          assignRolesError.response?.data?.message || 'Failed to update roles',
        variant: 'destructive',
      });
    } finally {
      setIsAssigningRoles(false);
    }
  };

  const handleSubmitUser = async (formData) => {
    try {
      setIsAddingUser(true);
      const response = await UserService.createAhedgeUser(formData);
      if (response.success) {
        toast({
          title: 'Success',
          description: 'User created successfully!',
          variant: 'default',
        });
        setShowAddModal(false);
        refetch();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create user. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (submitError) {
      console.error('Error adding user:', submitError);
      toast({
        title: 'Error',
        description:
          'An error occurred while creating the user. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsAddingUser(false);
    }
  };

  const columns = getColumns(
    handleToggleStatus,
    isToggling,
    handleAssignRoles,
    isAssigningRoles
  );
  const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));

  const emptyState = (
    <div className="bg-card rounded-xl border border-gray-100 dark:border-border shadow-sm flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-5">
        <Users className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-tight">
        No users found
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs mb-5 leading-relaxed">
        Add your first team member to get started.
      </p>
      <button
        type="button"
        onClick={() => setShowAddModal(true)}
        className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer"
      >
        <UserRoundPlus className="w-3.5 h-3.5" strokeWidth={2} />
        Add User
      </button>
    </div>
  );

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>Error loading users: {error.message || error}</p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Ahedge Users"
        actions={
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserRoundPlus className="w-3.5 h-3.5" strokeWidth={2} />
            Add User
          </button>
        }
      />

      <PageContainer className="space-y-5">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] font-medium text-gray-500">Role:</span>
          <AppSelect
            options={roleOptions}
            value={selectedRole}
            onValueChange={handleRoleChange}
            className="h-8 text-xs w-[130px] bg-card"
          />
        </div>

        <DataTable
          className="rounded-xl overflow-hidden"
          data={users}
          columns={columns}
          loading={isLoading}
          tableLayout="auto"
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyState={emptyState}
          rowClassName={() =>
            'hover:bg-brand-bg-subtle transition-colors cursor-pointer'
          }
        />
      </PageContainer>

      <AddUserModal
        isOpen={showAddModal}
        onSubmit={handleSubmitUser}
        onClose={() => setShowAddModal(false)}
        loading={isAddingUser}
      />
    </div>
  );
};

export default AhedgeUsers;
